import * as R from 'ramda';
import { ActionType } from '../../core/redux/actionType';
import { makeApiAction, makeRsaaTypes, makeApiReducer, ApiReducer } from '../../core/api/rsaa';
import { Endpoints } from '../../core/api/endpoints';
import { getLoading, addLoading, addError, noLoadingNoError } from '../../core/api/module';


const KEY_ACTION = ActionType.GetECourseUrlRequest;
const ACTIONS    = [
  ActionType.GetECourseUrlRequest,
  ActionType.GetECourseUrlResponse,
  ActionType.GetECourseUrlError,
];


type Payload = string;


type Meta = {
  projectId: number;
};


type EnrollmentOverrides = {
  registered?: boolean;
  courseIdStr?: string;
  moodleUserId?: number;
};


/**
 * Dispatch this action to retrieve the contact details for the participant's current project
 *
 * @param projectId - the database Id of the project whose contact info we want
 * @param participantId - the database Id of the participant whose contact info we want
 * @param overrides - when supplied (from a prior /eCourse/preEnroll call), the server skips
 *                    the IsEnrolled check and looks up the Moodle user by id.
*/
export const getECourseUrlAction = (
  projectId: number,
  participantId: number,
  overrides?: EnrollmentOverrides,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.GetECourseUrl,
    headers:  { 'content-type': 'application/json', 'accept': 'application/json' },
    body:     JSON.stringify({
      projectId,
      participantId,
      ...(overrides?.registered !== undefined && { registered: overrides.registered }),
      ...(overrides?.courseIdStr !== undefined && { courseIdStr: overrides.courseIdStr }),
      ...(overrides?.moodleUserId !== undefined && { moodleUserId: overrides.moodleUserId }),
    }),
    types:    makeRsaaTypes<Payload, Meta>(ACTIONS, { projectId }),
    bailout:  state => !!getLoading(state, KEY_ACTION, `${projectId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const { projectId } = action.meta;
  const metaTag       = `${projectId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  if (!payload) {
    return successState;
  }

  return R.assoc('eCourseUrl', payload, successState);
};

export const getECourseUrlReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
